import { useMemo } from 'react';
import {
  Collection,
  CollectionSchema,
} from '../../../../../services/entities/impl/blfApi/schemas/collection';
import {
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../../utils/validation/getValidationCounts';
import {
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';

/**
 * Hook for validating collection data
 *
 * @param collection The collection to validate
 * @returns Validation result and summary
 */
export const useCollectionValidation = (
  collection: Collection | undefined
): {
  validationResult: ValidationResult;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    // Check for empty collection data
    if (!collection) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message:
              'No collection data found. API endpoint may have failed or collection.json was not uploaded.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        validationResult: emptyValidationResult,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // 1. Validate collection against schema
    const validationResult = validateSchema(collection, CollectionSchema);

    // 2. Get validation counts
    const counts = getValidationCounts(validationResult);

    // 3. Create summary result with messages
    const summaryResult = createValidationSummary(counts);

    return {
      validationResult,
      summary: {
        result: summaryResult,
        counts,
      },
    };
  }, [collection]);
};
